import { ChangeDetectionStrategy, Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import {
    debounceTime,
    distinctUntilChanged,
    forkJoin,
    map,
    of,
    Subject,
    switchMap,
    takeUntil,
} from 'rxjs';
import { PATHS } from 'src-ironside/app-routing.module';
import { TranslateDynamicLanguagePipe } from 'src-ironside/pipes/translateDynamicLanguage.pipe';
import { Category, CategoryRepository } from 'src-ironside/repositories/category.repository';
import { Country, CountryRepository } from 'src-ironside/repositories/country.repository';
import { Department, DepartmentRepository } from 'src-ironside/repositories/department.repository';
import { News, NewsRepository } from 'src-ironside/repositories/news.repository';
import { Partner, PartnerRepository } from 'src-ironside/repositories/partner.repository';
import {
    DynamicProductParam,
    Product,
    ProductExtendInfo,
    ProductRepository,
} from 'src-ironside/repositories/product.repository';
import { ResourceRepository } from 'src-ironside/repositories/resource.repository';
import {
    DynamicReferencedFilterParam,
    Supplier,
    SupplierRepository,
} from 'src-ironside/repositories/supplier.repository';
import { FilterData } from 'src-ironside/services/accueil-data.service';
import { CommonService } from 'src-ironside/services/common.service';
import { PermissionService } from 'src-ironside/services/permission.service';
import { ScreenMediator } from 'src-ironside/services/screen.mediator';
import { SessionStorageService } from 'src-ironside/services/session-storage.service';
import { NewsDetailDialogComponent } from 'src-ironside/shared/components/dialogs/news-detail-dialog/news-detail-dialog.component';
import {
    buildCategoryHierarchyRandom,
    findCategoryLv1ByCategories,
    getParamSearchFilters,
    processInput,
} from 'src-ironside/utils/categories';
import { getCurrentDate } from 'src-ironside/utils/date.utils';
import { UserService } from 'src/app/admin/core/services/user.service';
import { CustomTranslateService } from 'src/app/shared/services/custom-translate.service';
import { DynamicDocumentService } from 'src/app/shared/services/dynamic-document.service';
import { environment } from 'src/environments/environment.ironside';

@Component({
    selector: 'app-accueil',
    templateUrl: './accueil.component.html',
    styleUrls: ['accueil.component.css'],
    providers: [
        CategoryRepository,
        SupplierRepository,
        CountryRepository,
        ProductRepository,
        DepartmentRepository,
        PartnerRepository,
        NewsRepository,
        ResourceRepository,
        TranslateDynamicLanguagePipe,
    ],
    changeDetection: ChangeDetectionStrategy.Default,
})
export class AccueilComponent implements OnInit {
    @ViewChild('searchProduct') searchProduct: any;

    readonly unsubscribe$ = new Subject();
    readonly roleUser = localStorage.getItem('role');
    name: string;
    hasNews?: boolean;
    dataViewingFrames: any[] = [
        {
            dateImport: '03/05/2024',
            nameFile: 'nom du fichier.xlsx',
            status: 'Validé',
        },
        {
            dateImport: '03/05/2024',
            nameFile: 'nom du fichier.xlsx',
            status: 'En cours',
        },
        {
            dateImport: '03/05/2024',
            nameFile: 'nom du fichier.xlsx',
            status: 'Validé',
        },
        {
            dateImport: '03/05/2024',
            nameFile: 'nom du fichier.xlsx',
            status: 'En cours',
        },
        {
            dateImport: '03/05/2024',
            nameFile: 'nom du fichier.xlsx',
            status: 'Validé',
        },
    ];
    searchForm: FormGroup = this.fb.group<FilterData>({
        categories: [],
        departments: '',
        research: '',
        createDate: '',
        suppliers: [],
        partners: [],
        isNewProduct: false,
    });

    autocompleteSearchlistProducts: any[] = [];

    extranetRestrictions: { champs: string[]; fonctions: string[]; menu: string[] } = {
        champs: [],
        fonctions: [],
        menu: [],
    };

    environment = environment;
    currentLang =
        environment.languagesExtranet.find((lang) => lang.code === this.translate.currentLang)
            ?.locale || 'en_GB';

    constructor(
        private categoryRepository: CategoryRepository,

        private productRepository: ProductRepository,
        private newsRepository: NewsRepository,
        private fb: FormBuilder,
        private screenMediator: ScreenMediator,
        private userService: UserService,
        private router: Router,
        private dialog: MatDialog,
        private dynamicDocumentService: DynamicDocumentService,
        private sessionStorageService: SessionStorageService,
        private _commonService: CommonService,
        public translateService: CustomTranslateService,
        private translateDynamicLanguage: TranslateDynamicLanguagePipe,
        private translate: TranslateService,
        public permissionService: PermissionService
    ) {}

    ngOnInit(): void {
        if (!this.userService.checklogin()) {
            this.router.navigate([PATHS.logout]);
        }
        this.setName();
        this.responsiveOptions = [
            {
                breakpoint: '1024px',
                numVisible: 3,
                numScroll: 1,
            },
            {
                breakpoint: '767px',
                numVisible: 2,
                numScroll: 1,
            },
            {
                breakpoint: '560px',
                numVisible: 1,
                numScroll: 1,
            },
        ];

        this.getCategories();
        this.getSuppliers();
        this.getDepartments();
        this.getYellowFocus();
        this.getPartners();
        this.setSearchPipe();

        this.searchForm.valueChanges
            .pipe(
                debounceTime(600),
                distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr))
            )
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((res) => {
                if (
                    (this.searchForm?.value?.research &&
                        this.searchForm?.value?.research.length > 0) ||
                    this.searchForm?.value?.isNewProduct ||
                    (this.searchForm?.value?.suppliers &&
                        this.searchForm?.value?.suppliers.length > 0)
                ) {
                    this.getCategoriesByFilter();
                } else {
                    this._commonService
                        .getCategoriesAccuei()
                        .pipe(takeUntil(this.unsubscribe$))
                        .subscribe((res) => {
                            this.listCategories = res.sort((a: any, b: any) =>
                                a.nameCategory?.[this.currentLang].localeCompare(
                                    b.nameCategory?.[this.currentLang]
                                )
                            );
                        });
                }
            });
        this._commonService.getExtranetRestrictionsStore().subscribe((res) => {
            this.extranetRestrictions = res;
        });
        this.getNewProducts();
    }

    setName() {
        let prenom = localStorage.getItem('name')
            ? localStorage.getItem('name')
            : sessionStorage.getItem('name')
            ? sessionStorage.getItem('name')
            : '';
        let nom = localStorage.getItem('surname')
            ? localStorage.getItem('surname')
            : sessionStorage.getItem('surname')
            ? sessionStorage.getItem('surname')
            : '';

        this.name = `${prenom} ${nom}`;
    }

    setNews(event: any) {
        this.hasNews = event;
    }
    // code here
    cli: string = 'non';
    responsiveOptions!: any[];
    isAdvancedActive: boolean = false;
    listCategories: Category[];
    listSuppliers: Supplier[];
    listCountry: Country[];
    departments: Department[];
    partners: Partner[];
    newProducts: Product[];
    news: News[];

    submitHandler() {
        const selectedCategory = this.listCategories?.find(
            (cate) => cate.id === this.searchForm.value.categories
        );
        const resultCategory = [selectedCategory?.id];
        selectedCategory?.subCategory?.forEach((el: any) => {
            resultCategory.push(el?.id);
        });

        const searchFormValue = {
            ...this.searchForm.value,
            suppliers: this.searchForm.value.suppliers ? this.searchForm.value.suppliers : [],
        };
        if (this.searchForm.value.categories) {
            searchFormValue.categories = resultCategory;
        }
        this.sessionStorageService.setFilters(searchFormValue);
        setTimeout(() => {
            this.screenMediator.toRecherche();
        }, 200);
    }

    getBodyFournisseurFilters() {
        let filters: any = [];

        const searchFormValue = {
            ...this.searchForm.value,
            isNewProduct: this.searchForm.value.isNewProduct === true ? true : false,
        };

        if (searchFormValue.research) {
            const research = searchFormValue.research;

            if (research) {
                filters = [...filters, ...getParamSearchFilters(research)];
            }
        }
        if (searchFormValue.isNewProduct && searchFormValue.isNewProduct === true) {
            filters.push(
                {
                    fieldName: 'newStartDate',
                    conditions: [
                        {
                            type: 'LESS_THAN',
                            value: getCurrentDate(),
                        },
                    ],
                },
                {
                    fieldName: 'newEndDate',
                    conditions: [
                        {
                            type: 'GREATER_THAN',
                            value: getCurrentDate(),
                        },
                    ],
                },
                {
                    fieldName: 'new',
                    conditions: [
                        {
                            type: 'EQUALS',
                            value: true,
                        },
                    ],
                }
            );
        }

        /* FOURNISSEUR */
        if (searchFormValue.suppliers && searchFormValue.suppliers.length > 0) {
            const conditions = searchFormValue.suppliers.map((el: string) => ({
                type: 'EQUALS',
                value: el,
            }));

            filters.push({
                fieldName: 'linkSupplier.documentId',
                conditions: conditions,
            });
        }

        filters.push({
            fieldName: 'statusProduct',
            conditions: [
                {
                    type: 'EQUALS',
                    value: 'VALIDE',
                },
            ],
        });

        return filters;
    }

    getRelativeCategoryCodes(category: Required<Category>) {
        const getSubCategoryCodes = (sCategory: Required<Category>): string[] => {
            if (!sCategory?.subCategory || sCategory?.subCategory?.length === 0) {
                return [sCategory.id];
            }
            return sCategory?.subCategory?.reduce<string[]>(
                (acc, current: any) => [...acc, ...(getSubCategoryCodes(current) ?? [])],
                [sCategory.id]
            );
        };
        return getSubCategoryCodes(category);
    }

    toggleAdvanced() {
        this.isAdvancedActive = !this.isAdvancedActive;
    }

    toProductDetail(id: string) {
        this.screenMediator.toProductDetail(id);
    }

    showNewsDetail(id: string) {
        this.dialog.open(NewsDetailDialogComponent, {
            width: '1100px',
            height: 'auto',
            data: { id: id },
        });
    }

    getCategories() {
        this.categoryRepository
            .findOriginal(3)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((res: any) => {
                const result = this.categoryRepository.transformAllLanguage(res?.content);
                this.listCategories = Array.from(result.values())
                    /* .sort((a: any, b: any) => {
                        if (a?.orderCategory === null || a?.orderCategory === undefined) return 1;
                        if (b?.orderCategory === null || b?.orderCategory === undefined) return -1;
                        return a?.orderCategory - b?.orderCategory;
                    }) */
                    .sort((a: any, b: any) =>
                        a.nameCategory?.[this.currentLang].localeCompare(
                            b.nameCategory?.[this.currentLang]
                        )
                    );

                this._commonService.setCategoriesAccueil(this.listCategories);
            });
    }

    getSuppliers() {
        const body: DynamicProductParam = {
            filters: [],
            fields: [],
            locales: [this.currentLang],
        };
        this.dynamicDocumentService
            .getDataParameterCountByDocumentName(body, 'suppliers')
            .pipe(
                map((res) => {
                    return res
                        .filter((el: any) => el?.supplierStatusInfo?.valeur === 'PUBLIE')
                        .map((el: any) => ({
                            count: el.count,
                            id: el.id,
                            title: el?.supplier?.companyName,
                            code: el?.supplier?.code,
                        }))
                        .sort((a: any, b: any) => a?.title?.localeCompare(b?.title));
                }),
                takeUntil(this.unsubscribe$)
            )
            .subscribe((res) => {
                this.listSuppliers = res.sort((a: any, b: any) =>
                    a?.title?.localeCompare(b?.title)
                );
            });
    }

    getDepartments() {
        const body: DynamicProductParam = {
            filters: [],
            fields: [],
            locales: [this.currentLang],
        };

        this.dynamicDocumentService
            .getDataParameterCountByDocumentName(body, 'departments')
            .pipe(
                map((res) => {
                    return res
                        .map((el: any) => ({
                            count: el.count,
                            id: el.id,
                            title: el?.department?.mapLocalLibelle,
                        }))
                        .sort((a: any, b: any) =>
                            a?.title?.[this.currentLang].localeCompare(b?.title?.[this.currentLang])
                        );
                }),
                takeUntil(this.unsubscribe$)
            )
            .subscribe((res) => {
                this.departments = res;
            });
    }

    getFilterTranslate(valueArray: any[]) {
        return valueArray
            ?.map((item) => ({
                ...item,
                translatedTitle: this.translateDynamicLanguage.transform(item.title),
            }))
            .sort((a: any, b: any) => a.translatedTitle.localeCompare(b.translatedTitle));
    }

    get departmentValue() {
        const values = this.searchForm?.get('departments')?.value;
        if (values && values.length > 0) {
            const result = values.map((el: string) => {
                return this.departments.find((item) => el === item.id);
            });
            return result;
        }
    }

    get supplierValue() {
        const values = this.searchForm?.get('suppliers')?.value;
        if (values && values.length > 0) {
            const result = values.map((el: string) => {
                return this.listSuppliers.find((item) => el === item.id);
            });
            return result;
        }
    }

    private isDataLoaded = false;

    getNewProducts() {
        const currentFilter = [
            {
                fieldName: 'newStartDate',
                conditions: [{ type: 'LESS_THAN', value: getCurrentDate() }],
            },
            {
                fieldName: 'newEndDate',
                conditions: [{ type: 'GREATER_THAN', value: getCurrentDate() }],
            },
            {
                fieldName: 'new',
                conditions: [{ type: 'EQUALS', value: true }],
            },
        ];

        const baseBody: DynamicProductParam = {
            filters: currentFilter,
            fields: [
                'id',
                'longDescription',
                'shortDescription',
                'brand',
                'packaging',
                'standardNumberNf',
                'state',
                'linkToTariff',
                'linkToRessources',
                'description',
                'referenceProduct',
                'linkToParentProduct',
                'namePartner',
                'linkToTariff',
                'countryOfManufacture',
                'cutomsCode',
                'gencodeProduct',
                'specification',
                'typeOfProduct',
                'new',
                'orderNumber',
                'statusProduct',
            ],
            locales: [this.currentLang],
            //sorts: { referenceProduct: 'DESC', publishedOrder: 'DESC' },
            page: 1,
            pageSize: 30,
        };

        this.dynamicDocumentService
            .getProductNewByResource(baseBody)
            .pipe(
                /* switchMap((firstRes: any) => {
                const totalPages = firstRes?.totalPages || 1;

                if (totalPages === 1) {
                    return of(firstRes); 
                }

                const randomPage = Math.floor(Math.random() * totalPages) + 1;

                const bodyWithRandomPage: DynamicProductParam = {
                    ...baseBody,
                    page: 1,
                    pageSize: 30, 
                };

                return this.dynamicDocumentService.getProductNewByResource(bodyWithRandomPage);
            }), */
                map((res: any) => {
                    const products = res?.content
                        ?.map((product: Product) =>
                            this.productRepository.convertProductOriginalLanguage(product)
                        )
                        .filter((el: Product) => el?.id);

                    return {
                        content: products,
                        meta: {
                            pageSize: res?.size,
                            totalPages: res?.totalPages,
                            totalItems: res?.totalElements,
                        },
                    };
                }),
                takeUntil(this.unsubscribe$)
            )
            .subscribe({
                next: (res) => {
                    if (!this.isDataLoaded) {
                        this.isDataLoaded = true;
                        this.productRepository
                            .loadFullExtendDataByGroup(res?.content, false, baseBody)
                            .subscribe({
                                next: (res) => {
                                    this.newProducts =
                                        res || []; /* (res || []).sort((a: any, b: any) => {
                                        if (a?.orderNumber === null || a?.orderNumber === undefined) return 1;
                                        if (b?.orderNumber === null || b?.orderNumber === undefined) return -1;
                                        return a?.orderNumber - b?.orderNumber;
                                    }); */
                                },
                                error: (error) => {
                                    console.error('Error loading product data:', error);
                                },
                            });
                    }
                },
            });
    }

    getYellowFocus() {
        this.newsRepository
            ?.find()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((res) => {
                this.news = res.content.map((iNew) => {
                    return { ...iNew, imageURL: iNew?.photo?.[0]?.url ?? iNew?.document?.[0]?.url };
                });
            });
    }

    getPartners() {
        const body: DynamicProductParam = {
            filters: [],
            fields: [],
            locales: [this.currentLang],
        };

        this.dynamicDocumentService
            .getDataParameterCountByDocumentName(body, 'partners')
            .pipe(
                map((res) => {
                    return res
                        .filter((el: any) => el?.partnerStatusInfo?.valeur === 'ACTIF')
                        .map((el: any) => ({
                            count: el.count,
                            id: el.id,
                            title: el?.partner?.companyName,
                            code: el?.partner?.code,
                        }))
                        .sort((a: any, b: any) => a?.title?.localeCompare(b?.title));
                }),
                takeUntil(this.unsubscribe$)
            )
            .subscribe((res) => {
                this.partners = res;
            });
    }

    // global search

    private searchProduct$ = new Subject<string>();

    setSearchPipe() {
        this.searchProduct$
            .pipe(debounceTime(600), distinctUntilChanged())
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((searchProduct: string) => {
                if (searchProduct) {
                    this.autocomplete(searchProduct);
                } else {
                    this.autocompleteSearchlistProducts = [];
                }
            });
    }

    autocomplete(term: string) {
        const regex = /^(\d+[,\s]+)*\d+$/;
        /* Check isReference */
        const result = regex.test(term) ? processInput(term) ?? [] : [term];
        const conditions = result.map((el) => ({
            type: 'CONTAINS',
            value: el,
        }));
        const lang =
            environment.languagesExtranet.find((lang) => lang.code === this.translate.currentLang)
                ?.locale || 'en_GB';

        let filters = [
            {
                fieldName: 'referenceProduct',
                operator: 'OR',
                conditions: conditions,
            },
            {
                fieldName: `shortDescription.${lang}`,
                operator: 'OR',
                conditions: conditions,
            },
            {
                fieldName: `longDescription.${lang}`,
                operator: 'OR',
                conditions: conditions,
            },
        ];

        const filters2 = [
            {
                fieldName: 'typeOfProduct',
                conditions: [
                    {
                        type: 'EQUALS',
                        value: 'FILS',
                    },
                ],
            },
            {
                fieldName: 'statusProduct',
                conditions: [
                    {
                        type: 'EQUALS',
                        value: 'VALIDE',
                    },
                ],
            },
        ];

        const requestPayload: any = {
            collectionName: 'dynamic_product',
            filters: filters,
            sorts: { uniqueId: 'DESC' },
            page: 1,
            pageSize: 100,
            operator: 'AND',
            filters2: filters2,
            locales: [this.currentLang],
        };
        this.dynamicDocumentService
            .findProductSpecial(requestPayload, true)
            .pipe(
                switchMap((res) => {
                    if (!res?.content?.length)
                        return of({
                            original: res,
                            statusList: [],
                            departmentList: [],
                            supplierList: [],
                            partnerList: [],
                        });

                    const filters = res.content.map((el: any) => ({
                        fieldName: 'id',
                        conditions: [
                            {
                                type: 'EQUALS',
                                value: el.linkToParentProduct?.[0]?.documentId,
                            },
                        ],
                    }));

                    const filtersParameter = res.content.map((el: any) => ({
                        fieldName: 'id',
                        conditions: [
                            {
                                type: 'EQUALS',
                                value: el?.id,
                            },
                        ],
                    }));

                    const body: DynamicProductParam = {
                        filters: filtersParameter,
                        fields: [],
                        locales: [
                            environment.languagesExtranet.find(
                                (lang) => lang.code === this.translate.currentLang
                            )?.locale || 'en_GB',
                        ],
                        sorts: {
                            referenceProduct: 'DESC',
                            publishedOrder: 'DESC',
                        },
                    };

                    const bodyParameter: DynamicProductParam = {
                        ...body,
                        filters: filters,
                        fields: ['id', 'statusProduct', 'linkToParentProduct', 'statusProductInfo'],
                    };

                    return forkJoin({
                        original: of(res),
                        statusList: this.dynamicDocumentService.getStatusById(bodyParameter),
                    });
                }),
                takeUntil(this.unsubscribe$)
            )
            .subscribe((res) => {
                const { original, statusList } = res;
                const result = original?.content
                    ?.map((el: any) => {
                        const status = statusList?.find((statusItem: any) =>
                            statusItem.linkToParentProduct?.some(
                                (link: any) => link.documentId === el.id
                            )
                        );
                        return {
                            ...el,
                            statusProductParent: status?.statusProductInfo?.valeur,
                        };
                    })
                    .filter((el: any) => el.statusProductParent === 'VALIDE');

                this.autocompleteSearchlistProducts = result
                    ?.map((el: Product) =>
                        this.productRepository.convertProductOriginalLanguage(el)
                    )
                    .slice(0, 5);
                this.onChange();
            });
    }

    onSearchInput(): void {
        this.searchProduct$.next(this.searchForm.value.research);
    }

    selectItem(item: any): void {
        if (!item?.id) {
            this.searchForm.patchValue({
                research: item?.longDescription ?? '',
            });
            return;
        }
        this.productRepository.findDetailById(item?.linkedProducts?.[0])?.subscribe((res) => {
            this.router.navigateByUrl(`/product/detail/${res?.referenceProduct}`);
        });
    }

    emptyList() {
        setTimeout(() => {
            this.autocompleteSearchlistProducts = [];
        }, 300);
    }

    getCategoriesByFilter() {
        const filters = this.getBodyFournisseurFilters();
        const body: DynamicReferencedFilterParam = {
            page: 1,
            pageSize: 100000,
            fields: ['nameCategory', 'categoryLevelCategory', 'attachmentCategory', 'codeCategory'],
            referencedDocumentName: 'dynamic_product',
            referencedField: 'categoryCode',
            referencedFilters: filters,
            sorts: {},
            referencedLocales: [this.currentLang],
        };
        this.dynamicDocumentService
            .getDynamicReferencedByFilter(body, 'dynamic_category/parents')
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (resRef: any) => {
                    this._commonService
                        .getCategories()
                        .pipe(takeUntil(this.unsubscribe$))
                        .subscribe((tree) => {
                            const treeResult = findCategoryLv1ByCategories(tree, resRef?.content);
                            const result = buildCategoryHierarchyRandom(treeResult);
                            this.listCategories = result
                                .map((el) => ({
                                    id: el?.id,
                                    title: el?.nameCategory,
                                    orderCategory: el.orderCategory,
                                }))
                                .sort((a: any, b: any) =>
                                    a.nameCategory?.[this.currentLang].localeCompare(
                                        b.nameCategory?.[this.currentLang]
                                    )
                                );
                        });
                },
                error: () => {},
            });
    }

    onChange() {
        this.searchForm.get('categories')?.reset();
    }

    getResourceValid(value: ProductExtendInfo[]) {
        if (Array.isArray(value) && value.length) {
            const result = value.flatMap((el: ProductExtendInfo) => {
                return (
                    el?.resources?.filter(
                        (item) => item?.files && item?.typeOfResource === 'PHOTO'
                    ) || []
                );
            });
            return result.length
                ? result?.[0]?.files?.[0]?.thumbnailUrl
                : 'src-ironside/assets/images/image-default.jpg';
        } else {
            return 'src-ironside/assets/images/image-default.jpg';
        }
    }

    isPermissionFonctions(role: any) {
        return this.extranetRestrictions?.fonctions?.includes(role);
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next(null);
        this.unsubscribe$.complete();
    }
}
